import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { slug: 'basics-of-trading', label: 'Basics of Trading' },
  { slug: 'fundamental-analysis', label: 'Fundamental Analysis' },
  { slug: 'technical-analysis', label: 'Technical Analysis' },
  { slug: 'traders-glossary', label: 'Traders Glossary' },
  { slug: 'trader-psychology', label: 'Trader Psychology' },
  { slug: 'cme-education', label: 'CME Group Education' },
  { slug: 'articles', label: 'Articles' },
  { slug: 'video-webinars', label: 'Video & Webinars' },
  { slug: 'fxpro-webinars', label: 'FxPro Webinars' },
  { slug: 'watch-and-learn', label: 'Watch and Learn' },
];

const AdminKnowledgeHub = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', slug: '', category_slug: 'articles', excerpt: '', content: '',
    image_url: '', author: 'WellingtonCDFS Editorial', language: 'en',
    status: 'DRAFT', featured: false, content_type: 'ARTICLE', video_url: ''
  });

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/admin/knowledge/articles`);
      setArticles(response.data);
    } catch (e) {
      toast.error('Failed to load articles');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchArticles(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      title: '', slug: '', category_slug: 'articles', excerpt: '', content: '',
      image_url: '', author: 'WellingtonCDFS Editorial', language: 'en',
      status: 'DRAFT', featured: false, content_type: 'ARTICLE', video_url: ''
    });
    setShowEditor(true);
  };

  const openEdit = (article) => {
    setEditing(article);
    setForm({
      title: article.title || '', slug: article.slug || '', category_slug: article.category_slug || 'articles',
      excerpt: article.excerpt || '', content: article.content || '', image_url: article.image_url || '',
      author: article.author || '', language: article.language || 'en', status: article.status || 'DRAFT',
      featured: !!article.featured, content_type: article.content_type || 'ARTICLE', video_url: article.video_url || ''
    });
    setShowEditor(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/knowledge/articles/${editing.id}`, form);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/admin/knowledge/articles`, form);
        toast.success('Article created');
      }
      setShowEditor(false);
      fetchArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/admin/knowledge/articles/${id}`);
      toast.success('Article deleted');
      fetchArticles();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="p-8" data-testid="admin-knowledge-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Knowledge Hub</h1>
          <p className="text-slate-600 mt-1">{articles.length} articles</p>
        </div>
        <Button onClick={openNew} data-testid="admin-new-article-btn"><Plus size={18} className="mr-2" /> New Article</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-4">Title</th>
              <th className="p-4">Category</th>
              <th className="p-4">Language</th>
              <th className="p-4">Status</th>
              <th className="p-4">Featured</th>
              <th className="p-4">Created</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
              : articles.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-slate-500">No articles yet — create one!</td></tr>
              : articles.map(a => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-4 font-medium">{a.title}</td>
                  <td className="p-4"><span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{a.category_slug}</span></td>
                  <td className="p-4 uppercase">{a.language}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      a.status === 'PUBLISHED' ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-slate-100 text-slate-600'
                    }`}>{a.status}</span>
                  </td>
                  <td className="p-4">{a.featured ? '⭐' : '-'}</td>
                  <td className="p-4 text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`edit-article-${a.id}`}><PencilSimple size={14} /></Button>
                      <Button size="sm" variant="destructive" onClick={() => remove(a.id)} data-testid={`delete-article-${a.id}`}><Trash size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="article-title-input" />
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder="auto-generated if empty" data-testid="article-slug-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category_slug} onValueChange={v => setForm({...form, category_slug: v})}>
                  <SelectTrigger data-testid="article-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Language</Label>
                <Select value={form.language} onValueChange={v => setForm({...form, language: v})}>
                  <SelectTrigger data-testid="article-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="it">Italian</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.content_type} onValueChange={v => setForm({...form, content_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARTICLE">Article</SelectItem>
                    <SelectItem value="VIDEO">Video</SelectItem>
                    <SelectItem value="WEBINAR">Webinar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Excerpt (short summary)</Label>
              <Textarea rows={2} value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} data-testid="article-excerpt-input" />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea rows={10} value={form.content} onChange={e => setForm({...form, content: e.target.value})} data-testid="article-content-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Image URL (optional)</Label>
                <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} />
              </div>
              <div>
                <Label>Video URL (optional)</Label>
                <Input value={form.video_url} onChange={e => setForm({...form, video_url: e.target.value})} placeholder="https://youtube.com/embed/..." />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 items-center">
              <div>
                <Label>Author</Label>
                <Input value={form.author} onChange={e => setForm({...form, author: e.target.value})} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger data-testid="article-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                    <SelectItem value="PUBLISHED">Published</SelectItem>
                    <SelectItem value="ARCHIVED">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.featured} onCheckedChange={v => setForm({...form, featured: v})} data-testid="article-featured-switch" />
                <Label>Featured</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={save} data-testid="article-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminKnowledgeHub;
